import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { outline } from 'modules/helpers/mixins';
import { chipSizeStyling } from './shared/chipSizes';
import { chipVariantStyling } from './shared/chipVariants';
import type { ChipProps, ControlledChipProps, UncontrolledChipProps } from './shared/types';

// #region Styles
export const chipDefaultStyling = css`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    margin: 0;

    &:has(:disabled) {
        cursor: not-allowed;
        pointer-events: none;
    }

    &:has(:focus-visible) {
        ${outline}
    }
`;

const Label = styled.label<{ $size: ChipProps['size']; $variant: ChipProps['variant'] }>`
    ${chipDefaultStyling}
    ${chipSizeStyling}
    ${chipVariantStyling}
`;

const Content = styled.span<{ $wrap: boolean }>`
    display: flex;
    flex-direction: column;
    user-select: none;
    ${({ $wrap }) =>
        !$wrap &&
        css`
            white-space: nowrap;
            text-overflow: ellipsis;
            overflow: hidden;
        `}
`;

const StyledInput = styled.input`
    width: 0;
    height: 0;
    opacity: 0;
    box-sizing: border-box;
    padding: 0;
`;
// #endregion Styles

const BaseChip = React.forwardRef<HTMLLabelElement, ChipProps>(({ size, variant, children, ...restProps }, ref) => {
    const { className } = restProps;

    return (
        <Label className={className} $size={size} $variant={variant} ref={ref}>
            {children}
        </Label>
    );
});

const ControlledChip = React.forwardRef<HTMLLabelElement, ControlledChipProps>(
    ({ onChange, checked, inputProps, children, wrap = false, ...restProps }, ref) => {
        return (
            <BaseChip ref={ref} {...restProps}>
                <StyledInput checked={checked} onChange={onChange} {...inputProps} />
                {children && <Content $wrap={wrap}>{children}</Content>}
            </BaseChip>
        );
    }
);

const UncontrolledChip = React.forwardRef<HTMLLabelElement, UncontrolledChipProps>(
    ({ onChange, children, defaultChecked, inputProps, ...restProps }, ref) => {
        return (
            <ControlledChip inputProps={{ ...inputProps, defaultChecked }} {...restProps} ref={ref}>
                {children}
            </ControlledChip>
        );
    }
);

const Chip = Object.assign(UncontrolledChip, {
    Controlled: ControlledChip
});

export default Chip;
